import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { wrapFormikHandleChange } from "../../../../infrastructure/utils/formatters";

import UserManagementService from "../../../../infrastructure/api/userManagement";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";

const userValidationSchema = Yup.object().shape({
  nationalCode: Yup.string().trim(),
  mobile: Yup.string().trim(),
});

const requestValidationSchema = Yup.object().shape({
  requestNumber: Yup.string()
    .trim()
    .matches(/^\d+$/, "فقط عدد وارد کنید")
    .required("شماره درخواست الزامی است"),
});

const mobileChangeValidationSchema = Yup.object().shape({
  oldMobile: Yup.string().trim().required("موبایل فعلی الزامی است"),
  newMobile: Yup.string().trim().required("موبایل جدید الزامی است"),
});

const InfoLine = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.5 }}>
    <Box component="span" color="text.secondary">
      {label}:{" "}
    </Box>
    <Box component="span" fontWeight={600}>
      {value == null || value === "" ? "—" : String(value)}
    </Box>
  </Typography>
);

const normalizeUserPreview = (raw) => {
  if (!raw) return null;
  const users = raw.users ?? raw.Users ?? [];
  const requests = raw.requests ?? raw.Requests ?? [];
  return {
    found: Boolean(raw.found ?? raw.Found),
    isAdminProtected: Boolean(raw.isAdminProtected ?? raw.IsAdminProtected),
    message: raw.message ?? raw.Message ?? "",
    matchedUserCount: raw.matchedUserCount ?? raw.MatchedUserCount ?? users.length,
    requestCount: raw.requestCount ?? raw.RequestCount ?? requests.length,
    contractorRegistrationCount:
      raw.contractorRegistrationCount ?? raw.ContractorRegistrationCount ?? 0,
    users: users.map((u) => ({
      userId: u.userId ?? u.UserId,
      fullName: u.fullName ?? u.FullName,
      userName: u.userName ?? u.UserName,
      mobile: u.mobile ?? u.Mobile,
      nationalCode: u.nationalCode ?? u.NationalCode,
      roleTitle: u.roleTitle ?? u.RoleTitle,
      userKindTitle: u.userKindTitle ?? u.UserKindTitle,
      isActive: u.isActive ?? u.IsActive,
      requestCount: u.requestCount ?? u.RequestCount ?? 0,
    })),
    requests: requests.map((r) => ({
      requestId: r.requestId ?? r.RequestId,
      referenceId: r.referenceId ?? r.ReferenceId,
      applicantName: r.applicantName ?? r.ApplicantName,
      requestConfirmTitle: r.requestConfirmTitle ?? r.RequestConfirmTitle,
      plantCapacityTitle: r.plantCapacityTitle ?? r.PlantCapacityTitle,
    })),
  };
};

const normalizeRequestPreview = (raw) => {
  if (!raw) return null;
  return {
    found: Boolean(raw.found ?? raw.Found),
    message: raw.message ?? raw.Message ?? "",
    requestId: raw.requestId ?? raw.RequestId,
    referenceId: raw.referenceId ?? raw.ReferenceId,
    applicantName: raw.applicantName ?? raw.ApplicantName,
    applicantMobile: raw.applicantMobile ?? raw.ApplicantMobile,
    applicantNationalCode:
      raw.applicantNationalCode ?? raw.ApplicantNationalCode,
    requestConfirmTitle: raw.requestConfirmTitle ?? raw.RequestConfirmTitle,
    plantCapacityTitle: raw.plantCapacityTitle ?? raw.PlantCapacityTitle,
    address: raw.address ?? raw.Address,
    userId: raw.userId ?? raw.UserId,
    userFullName: raw.userFullName ?? raw.UserFullName,
  };
};

const DeleteAllUserDataPage = () => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestPreviewLoading, setRequestPreviewLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [mobileChangeLoading, setMobileChangeLoading] = useState(false);

  const [resultMessage, setResultMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(null);
  const [userPreview, setUserPreview] = useState(null);
  const [pendingUserDelete, setPendingUserDelete] = useState(null);

  const [requestResultMessage, setRequestResultMessage] = useState(null);
  const [requestIsSuccess, setRequestIsSuccess] = useState(null);
  const [requestPreview, setRequestPreview] = useState(null);
  const [pendingRequestDelete, setPendingRequestDelete] = useState(null);

  const [mobileChangeResultMessage, setMobileChangeResultMessage] = useState(null);
  const [mobileChangeIsSuccess, setMobileChangeIsSuccess] = useState(null);

  const onSyncMissingUserInfo = async () => {
    setSyncLoading(true);
    setResultMessage(null);
    setIsSuccess(null);
    try {
      const res = await UserManagementService.syncMissingUserInfoApi();
      const success = res?.isSuccess === true;
      setIsSuccess(success);
      const msg = res?.message || res?.data?.message;
      setResultMessage(
        msg ||
          (success
            ? "همگام‌سازی با موفقیت انجام شد. صفحه userinfo را رفرش کنید."
            : "خطا در همگام‌سازی.")
      );
    } catch (err) {
      setIsSuccess(false);
      setResultMessage(
        err?.response?.data?.message || err?.message || "خطا در ارتباط با سرور."
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const onPreviewUser = async (values) => {
    const nationalCode = values.nationalCode?.trim();
    const mobile = values.mobile?.trim();
    if (!nationalCode && !mobile) {
      setIsSuccess(false);
      setResultMessage("لطفاً کد ملی یا شماره موبایل را وارد کنید.");
      setUserPreview(null);
      setPendingUserDelete(null);
      return;
    }

    setPreviewLoading(true);
    setResultMessage(null);
    setIsSuccess(null);
    setUserPreview(null);
    setPendingUserDelete(null);

    try {
      const res = await UserManagementService.previewDeleteAllUserDataApi({
        nationalCode: nationalCode || null,
        mobile: mobile || null,
      });
      const preview = normalizeUserPreview(res?.data ?? res?.Data ?? null);
      const success = Boolean(preview?.found);
      setIsSuccess(success);
      setUserPreview(preview);
      if (preview?.found) {
        setResultMessage(null);
        setPendingUserDelete({
          nationalCode: nationalCode || null,
          mobile: mobile || null,
        });
      } else {
        setResultMessage(res?.message || preview?.message || "کاربر یافت نشد.");
      }
    } catch (err) {
      setIsSuccess(false);
      setResultMessage(
        err?.response?.data?.message || err?.message || "خطا در ارتباط با سرور."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const onConfirmDeleteUser = async () => {
    if (!pendingUserDelete) return;

    setLoading(true);
    setResultMessage(null);
    setIsSuccess(null);

    try {
      const res = await UserManagementService.deleteAllUserDataApi({
        nationalCode: pendingUserDelete.nationalCode,
        mobile: pendingUserDelete.mobile,
        deleteUserAccount: true,
      });
      const success = res?.isSuccess === true;
      setIsSuccess(success);
      setResultMessage(
        res?.message || (success ? "عملیات با موفقیت انجام شد." : "خطا در اجرا.")
      );
      if (success) {
        setUserPreview(null);
        setPendingUserDelete(null);
      }
    } catch (err) {
      setIsSuccess(false);
      setResultMessage(
        err?.response?.data?.message || err?.message || "خطا در ارتباط با سرور."
      );
    } finally {
      setLoading(false);
    }
  };

  const onPreviewRequest = async (values) => {
    const raw = values.requestNumber?.trim();
    const requestNumber = Number(raw);
    if (!Number.isFinite(requestNumber) || requestNumber <= 0) {
      setRequestIsSuccess(false);
      setRequestResultMessage("شماره درخواست معتبر نیست.");
      setRequestPreview(null);
      setPendingRequestDelete(null);
      return;
    }

    setRequestPreviewLoading(true);
    setRequestResultMessage(null);
    setRequestIsSuccess(null);
    setRequestPreview(null);
    setPendingRequestDelete(null);

    try {
      const res = await UserManagementService.previewDeleteRequestDataApi({
        requestNumber,
      });
      const preview = normalizeRequestPreview(res?.data ?? res?.Data ?? null);
      const success = Boolean(preview?.found);
      setRequestIsSuccess(success);
      setRequestPreview(preview);
      if (preview?.found) {
        setRequestResultMessage(null);
        setPendingRequestDelete({ requestNumber });
      } else {
        setRequestResultMessage(
          res?.message || preview?.message || "درخواست یافت نشد."
        );
      }
    } catch (err) {
      setRequestIsSuccess(false);
      setRequestResultMessage(
        err?.response?.data?.message || err?.message || "خطا در ارتباط با سرور."
      );
    } finally {
      setRequestPreviewLoading(false);
    }
  };

  const onConfirmDeleteRequest = async () => {
    if (!pendingRequestDelete) return;

    setRequestLoading(true);
    setRequestResultMessage(null);
    setRequestIsSuccess(null);

    try {
      const res = await UserManagementService.deleteRequestDataApi({
        requestNumber: pendingRequestDelete.requestNumber,
      });
      const success = res?.isSuccess === true;
      setRequestIsSuccess(success);
      setRequestResultMessage(
        res?.message || (success ? "درخواست حذف شد." : "خطا در حذف درخواست.")
      );
      if (success) {
        setRequestPreview(null);
        setPendingRequestDelete(null);
      }
    } catch (err) {
      setRequestIsSuccess(false);
      setRequestResultMessage(
        err?.response?.data?.message || err?.message || "خطا در ارتباط با سرور."
      );
    } finally {
      setRequestLoading(false);
    }
  };

  const onSubmitMobileChange = async (values) => {
    const oldMobile = values.oldMobile?.trim();
    const newMobile = values.newMobile?.trim();

    setMobileChangeLoading(true);
    setMobileChangeResultMessage(null);
    setMobileChangeIsSuccess(null);

    try {
      const res = await UserManagementService.changeUserMobileApi({
        oldMobile,
        newMobile,
      });

      const success = res?.isSuccess === true;
      setMobileChangeIsSuccess(success);
      setMobileChangeResultMessage(
        res?.message ||
          (success ? "شماره موبایل با موفقیت تغییر کرد." : "خطا در تغییر موبایل.")
      );
    } catch (err) {
      setMobileChangeIsSuccess(false);
      setMobileChangeResultMessage(
        err?.response?.data?.message || err?.message || "خطا در ارتباط با سرور."
      );
    } finally {
      setMobileChangeLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="تغییر شماره موبایل کاربر"
            subheader="این عملیات حساس است: نام کاربری نیز بر اساس موبایل تغییر می‌کند. فقط Admin مجاز است."
          />
          <CardContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              توجه: این عملیات موبایل را در Identity و جداول پروفایل مرتبط بروزرسانی می‌کند.
            </Alert>

            <Formik
              initialValues={{ oldMobile: "", newMobile: "" }}
              validationSchema={mobileChangeValidationSchema}
              onSubmit={onSubmitMobileChange}
            >
              {({ values, errors, touched, handleChange, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="موبایل فعلی"
                        name="oldMobile"
                        value={values.oldMobile}
                        onChange={wrapFormikHandleChange(handleChange)}
                        placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                        error={Boolean(touched.oldMobile && errors.oldMobile)}
                        helperText={
                          touched.oldMobile && errors.oldMobile
                            ? errors.oldMobile
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="موبایل جدید"
                        name="newMobile"
                        value={values.newMobile}
                        onChange={wrapFormikHandleChange(handleChange)}
                        placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                        error={Boolean(touched.newMobile && errors.newMobile)}
                        helperText={
                          touched.newMobile && errors.newMobile
                            ? errors.newMobile
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={mobileChangeLoading}
                        startIcon={
                          mobileChangeLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {mobileChangeLoading
                          ? "در حال بروزرسانی..."
                          : "تغییر موبایل"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>

            {mobileChangeResultMessage != null && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={mobileChangeIsSuccess ? "success" : "error"}>
                  {mobileChangeResultMessage}
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="حذف تمام داده‌های کاربر"
            subheader="ابتدا اطلاعات کاربر نمایش داده می‌شود؛ بعد از تأیید، حذف کامل انجام می‌شود."
          />
          <CardContent>
            <Formik
              initialValues={{ nationalCode: "", mobile: "" }}
              validationSchema={userValidationSchema}
              onSubmit={onPreviewUser}
            >
              {({ values, handleChange, handleSubmit, resetForm }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="کد ملی"
                        name="nationalCode"
                        value={values.nationalCode}
                        onChange={(e) => {
                          wrapFormikHandleChange(handleChange)(e);
                          setUserPreview(null);
                          setPendingUserDelete(null);
                        }}
                        placeholder="۱۰ رقم"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="شماره موبایل"
                        name="mobile"
                        value={values.mobile}
                        onChange={(e) => {
                          wrapFormikHandleChange(handleChange)(e);
                          setUserPreview(null);
                          setPendingUserDelete(null);
                        }}
                        placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        حذف از این فرم همیشه به صورت کامل انجام می‌شود (Hard Delete) و
                        حساب کاربری نیز از AspNetUsers حذف خواهد شد. نقش Admin محافظت
                        شده است.
                      </Alert>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={previewLoading || loading}
                        startIcon={
                          previewLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {previewLoading
                          ? "در حال جستجو..."
                          : "۱. نمایش اطلاعات"}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        color="secondary"
                        disabled={syncLoading}
                        onClick={onSyncMissingUserInfo}
                        startIcon={
                          syncLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {syncLoading ? "در حال سینک..." : "همگام‌سازی UserInfo"}
                      </Button>
                      {pendingUserDelete ? (
                        <Button
                          type="button"
                          variant="text"
                          onClick={() => {
                            setUserPreview(null);
                            setPendingUserDelete(null);
                            setResultMessage(null);
                            resetForm();
                          }}
                        >
                          انصراف
                        </Button>
                      ) : null}
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>

            {userPreview?.found ? (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {userPreview.message ||
                    `یافت شد: ${userPreview.matchedUserCount} کاربر، ${userPreview.requestCount} درخواست.`}
                </Alert>

                {(userPreview.users || []).map((user) => (
                  <Box
                    key={user.userId}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      {user.fullName || "بدون نام"}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine label="شناسه کاربر" value={user.userId} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine label="نام کاربری" value={user.userName} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine label="موبایل" value={user.mobile} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine label="کد ملی" value={user.nationalCode} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine label="نقش" value={user.roleTitle} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine label="نوع کاربر" value={user.userKindTitle} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine
                          label="وضعیت"
                          value={user.isActive ? "فعال" : "غیرفعال"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InfoLine
                          label="تعداد درخواست"
                          value={user.requestCount}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                {userPreview.contractorRegistrationCount > 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    ثبت‌نام پیمانکار مرتبط: {userPreview.contractorRegistrationCount}{" "}
                    مورد نیز حذف خواهد شد.
                  </Alert>
                ) : null}

                {(userPreview.requests || []).length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      درخواست‌هایی که حذف می‌شوند
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>کد پیگیری</TableCell>
                          <TableCell>Id</TableCell>
                          <TableCell>متقاضی</TableCell>
                          <TableCell>وضعیت</TableCell>
                          <TableCell>ظرفیت</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userPreview.requests.map((req) => (
                          <TableRow key={req.requestId}>
                            <TableCell>{req.referenceId}</TableCell>
                            <TableCell>{req.requestId}</TableCell>
                            <TableCell>{req.applicantName}</TableCell>
                            <TableCell>{req.requestConfirmTitle}</TableCell>
                            <TableCell>{req.plantCapacityTitle}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={loading || !pendingUserDelete}
                    onClick={onConfirmDeleteUser}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {loading
                      ? "در حال حذف..."
                      : "۲. تأیید و حذف کامل داده‌ها"}
                  </Button>
                </Stack>
              </Box>
            ) : null}

            {resultMessage != null && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={isSuccess ? "success" : "error"}>
                  {resultMessage}
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="حذف داده‌های یک درخواست"
            subheader="ابتدا مشخصات درخواست نمایش داده می‌شود؛ حساب کاربری مشتری باقی می‌ماند."
          />
          <CardContent>
            <Formik
              initialValues={{ requestNumber: "" }}
              validationSchema={requestValidationSchema}
              onSubmit={onPreviewRequest}
            >
              {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="شماره درخواست"
                        name="requestNumber"
                        value={values.requestNumber}
                        onChange={(e) => {
                          wrapFormikHandleChange(handleChange)(e);
                          setRequestPreview(null);
                          setPendingRequestDelete(null);
                        }}
                        placeholder="مثلاً 14008231 یا 8231"
                        error={Boolean(
                          touched.requestNumber && errors.requestNumber
                        )}
                        helperText={
                          touched.requestNumber && errors.requestNumber
                            ? errors.requestNumber
                            : "ابتدا Id داخلی، سپس ReferenceID جستجو می‌شود"
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={requestPreviewLoading || requestLoading}
                        startIcon={
                          requestPreviewLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {requestPreviewLoading
                          ? "در حال جستجو..."
                          : "۱. نمایش اطلاعات"}
                      </Button>
                      {pendingRequestDelete ? (
                        <Button
                          type="button"
                          variant="text"
                          onClick={() => {
                            setRequestPreview(null);
                            setPendingRequestDelete(null);
                            setRequestResultMessage(null);
                            resetForm();
                          }}
                        >
                          انصراف
                        </Button>
                      ) : null}
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>

            {requestPreview?.found ? (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "grey.50",
                }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  {requestPreview.message}
                </Alert>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine label="کد پیگیری" value={requestPreview.referenceId} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine label="شناسه داخلی" value={requestPreview.requestId} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine label="متقاضی" value={requestPreview.applicantName} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine label="موبایل" value={requestPreview.applicantMobile} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine
                      label="کد ملی"
                      value={requestPreview.applicantNationalCode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine
                      label="وضعیت"
                      value={requestPreview.requestConfirmTitle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLine
                      label="ظرفیت"
                      value={requestPreview.plantCapacityTitle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InfoLine label="آدرس" value={requestPreview.address} />
                  </Grid>
                </Grid>

                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  color="warning"
                  disabled={requestLoading || !pendingRequestDelete}
                  onClick={onConfirmDeleteRequest}
                  startIcon={
                    requestLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {requestLoading
                    ? "در حال حذف..."
                    : "۲. تأیید و حذف داده‌های درخواست"}
                </Button>
              </Box>
            ) : null}

            {requestResultMessage != null && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={requestIsSuccess ? "success" : "error"}>
                  {requestResultMessage}
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DeleteAllUserDataPage;
